/**
 * DashboardDrillDownPanel — KPI card drill-down overlay (Outstanding / Billed / Collected).
 */

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  fetchOutstandingPayApps,
  fetchBilledPayApps,
  fetchCollectedMtd,
  type OutstandingPayApp,
  type BilledPayApp,
  type CollectedPayment,
} from '../../api/dashboardDrilldown'
import { formatCurrency } from '../../lib/formatters'
import ModalPortal from '../../components/ModalPortal'

export type DrillDownView = 'outstanding' | 'billed' | 'collected'

interface Props {
  view: DrillDownView | null
  onClose: () => void
}

const TITLES: Record<DrillDownView, string> = {
  outstanding: 'Pending payments',
  billed:        'Billed pay applications',
  collected:     'Collected · this month',
}

function ageLabel(row: OutstandingPayApp): string {
  if (row.urgency === 'overdue' && row.days_overdue > 0) {
    return `${row.days_overdue}d overdue`
  }
  if (row.payment_due_date) {
    const due = new Date(row.payment_due_date)
    const now = new Date()
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (diff <= 0) return 'Due today'
    if (diff <= 7) return `Due in ${diff}d`
    return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return 'Awaiting payment'
}

function openPayApp(navigate: ReturnType<typeof useNavigate>, projectId: number, payAppId: number) {
  navigate(`/projects/${projectId}/pay-apps/${payAppId}?step=5`)
}

export default function DashboardDrillDownPanel({ view, onClose }: Props) {
  const navigate = useNavigate()

  const outstandingQ = useQuery({
    queryKey: ['drilldown-outstanding'],
    queryFn:  fetchOutstandingPayApps,
    enabled:  view === 'outstanding',
  })
  const billedQ = useQuery({
    queryKey: ['drilldown-billed'],
    queryFn:  fetchBilledPayApps,
    enabled:  view === 'billed',
  })
  const collectedQ = useQuery({
    queryKey: ['drilldown-collected'],
    queryFn:  fetchCollectedMtd,
    enabled:  view === 'collected',
  })

  if (!view) return null

  const loading = outstandingQ.isPending || billedQ.isPending || collectedQ.isPending
  const error = outstandingQ.error || billedQ.error || collectedQ.error

  return (
    <ModalPortal onBackdropClick={onClose} testId={`dash-drilldown-${view}`}>
      <div
        className="panel"
        style={{
          width: 'min(720px, 94vw)', maxHeight: '80vh', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', padding: 0,
        }}
      >
        <div className="panel-h" style={{ padding: '14px 18px', flexShrink: 0 }}>
          <div className="panel-tt">{TITLES[view]}</div>
          <button type="button" className="btn b-gh btn-sm" onClick={onClose}>Close</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0 18px 18px' }}>
          {loading && <p style={{ fontSize: 13, color: 'var(--ink4)' }}>Loading…</p>}
          {error && <p style={{ fontSize: 13, color: 'var(--red)' }}>Could not load list.</p>}

          {view === 'outstanding' && !loading && (
            <OutstandingTable
              rows={outstandingQ.data ?? []}
              onRow={(r) => { onClose(); openPayApp(navigate, r.project_id, r.id) }}
              onAria={(r) => { onClose(); navigate(`/aria/follow-ups?pa=${r.id}`) }}
            />
          )}
          {view === 'billed' && !loading && (
            <BilledTable
              rows={billedQ.data ?? []}
              onRow={(r) => { onClose(); openPayApp(navigate, r.project_id, r.id) }}
            />
          )}
          {view === 'collected' && !loading && (
            <CollectedTable
              rows={collectedQ.data ?? []}
              onRow={(r) => { onClose(); openPayApp(navigate, r.project_id, r.pay_app_id) }}
            />
          )}
        </div>
      </div>
    </ModalPortal>
  )
}

function OutstandingTable({
  rows, onRow, onAria,
}: {
  rows: OutstandingPayApp[]
  onRow: (r: OutstandingPayApp) => void
  onAria: (r: OutstandingPayApp) => void
}) {
  if (rows.length === 0) {
    return <p style={{ fontSize: 13, color: 'var(--ink4)' }}>No outstanding pay applications.</p>
  }
  return (
    <table className="tbl" data-testid="dash-drilldown-outstanding-table">
      <thead>
        <tr>
          <th>Project</th><th>Owner</th><th className="r">Amount</th><th>Age</th><th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onRow(r)}>
            <td><strong>{r.project_name}</strong><div style={{ fontSize: 10, color: 'var(--ink4)' }}>PA #{r.pay_app_number}</div></td>
            <td>{r.owner_name ?? '—'}</td>
            <td className="r mono-c" data-testid="dash-drilldown-money-cell">{formatCurrency(parseFloat(r.amount_due))}</td>
            <td><span className={`chip ${r.urgency === 'overdue' ? 'c-red' : r.urgency === 'due_soon' ? 'c-amb' : 'c-gry'}`}>{ageLabel(r)}</span></td>
            <td className="r" onClick={e => e.stopPropagation()}>
              <button type="button" className="btn b-ol btn-sm" data-testid={`dash-aria-reminder-${r.id}`}
                onClick={() => onAria(r)}>Draft reminder with Soren</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function BilledTable({ rows, onRow }: { rows: BilledPayApp[]; onRow: (r: BilledPayApp) => void }) {
  if (rows.length === 0) {
    return <p style={{ fontSize: 13, color: 'var(--ink4)' }}>No billed pay applications yet.</p>
  }
  return (
    <table className="tbl" data-testid="dash-drilldown-billed-table">
      <thead><tr><th>Project</th><th>Owner</th><th className="r">Amount</th><th>Status</th></tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onRow(r)}>
            <td><strong>{r.project_name}</strong><div style={{ fontSize: 10, color: 'var(--ink4)' }}>PA #{r.app_number}</div></td>
            <td>{r.owner ?? '—'}</td>
            <td className="r mono-c" data-testid="dash-drilldown-money-cell">{formatCurrency(parseFloat(r.amount_due))}</td>
            <td><span className="chip c-grn">{r.status}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function CollectedTable({ rows, onRow }: { rows: CollectedPayment[]; onRow: (r: CollectedPayment) => void }) {
  if (rows.length === 0) {
    return <p style={{ fontSize: 13, color: 'var(--ink4)' }}>No payments recorded this month.</p>
  }
  return (
    <table className="tbl" data-testid="dash-drilldown-collected-table">
      <thead><tr><th>Project</th><th>Method</th><th className="r">Amount</th><th>Date</th></tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onRow(r)}>
            <td><strong>{r.project_name}</strong><div style={{ fontSize: 10, color: 'var(--ink4)' }}>PA #{r.app_number}</div></td>
            <td>{r.source === 'manual' ? 'Check / manual' : r.payment_method ?? 'Stripe'}</td>
            <td className="r mono-c" data-testid="dash-drilldown-money-cell">{formatCurrency(parseFloat(r.amount))}</td>
            <td>{(r.paid_at ?? r.created_at) ? new Date(r.paid_at ?? r.created_at).toLocaleDateString() : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
